import type { FC } from "react";
import { useMemo, useState } from "react";

import { CountryDropdownButton, CountryDropdownItem, Dropdown } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";

import { SetupFieldLoader } from "./SetupFieldLoader";

interface ILLMSetupCountrySelectorProps {
    selectedCountry: ICountryObject;
    onCountriesChange: (countries: ICountryObject) => void;
    supportedCountriesByChatbot: Record<string, string[]>;
    isLoading: boolean;
}

export const LLMSetupCountrySelector: FC<ILLMSetupCountrySelectorProps> = ({
    selectedCountry,
    onCountriesChange,
    supportedCountriesByChatbot,
    isLoading,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const availableCountries: ICountryObject[] = useMemo(() => {
        if (!supportedCountriesByChatbot) {
            return [];
        }

        const allCountryCodes = new Set<string>();
        Object.values(supportedCountriesByChatbot).forEach((countries) => {
            countries.forEach((code) => allCountryCodes.add(code));
        });

        const uniqueCountryCodes = Array.from(allCountryCodes);
        const countries = CountryService.getCountriesByCodes(uniqueCountryCodes.map((code) => code.toLowerCase()));

        return countries
            .map((country) => ({
                id: country.id,
                text: country.text,
                code: country.code?.toUpperCase(),
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [supportedCountriesByChatbot]);

    const handleCountrySelect = (countryId: number) => {
        const country = availableCountries.find((item) => item.id === countryId);

        setSearchValue("");
        allTrackers.dispatchTrackEvent("Dropdown", "Select", `Ai Brand Visibility/Locations/${country.text}`);
        onCountriesChange(country);
    };

    const filteredCountries = useMemo(() => {
        if (!searchValue) {
            return availableCountries;
        }

        return availableCountries.filter((country) => country.text.toLowerCase().includes(searchValue.toLowerCase()));
    }, [availableCountries, searchValue]);

    const onToggle = (state: boolean) => {
        if (state) {
            allTrackers.dispatchTrackEvent("Dropdown", "Open", `Ai Brand Visibility/Location`);
        }

        setIsOpen(state);
    };

    return (
        <Box
            position="relative"
            borderRadius="3px"
            width="232px"
            height="40px"
        >
            {isLoading ? (
                <SetupFieldLoader />
            ) : (
                <Dropdown
                    isOpen={isOpen}
                    onToggle={onToggle}
                    onClick={({ id }) => handleCountrySelect(id)}
                    onSearch={(term) => setSearchValue(term)}
                    closeOnItemClick
                    appendTo="body"
                    hasSearch
                >
                    {[
                        <CountryDropdownButton
                            key="country-dropdown-button"
                            countryId={selectedCountry.id}
                        >
                            {selectedCountry.text}
                        </CountryDropdownButton>,
                        ...filteredCountries.map((country) => (
                            <CountryDropdownItem
                                key={country.id}
                                id={country.id}
                                text={country.text}
                            />
                        )),
                    ]}
                </Dropdown>
            )}
        </Box>
    );
};
